import React, { useContext, useEffect, useState } from "react";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Checkbox, IconButton, Paper } from "@mui/material";

import { Node } from "ui";

import { selectVersionConfig } from "fond/api";
import { LayerConfig, SublayerConfig } from "fond/types/ProjectLayerConfig";
import { useAppSelector } from "fond/utils/hooks";

import { InferredIcon } from "../Legend";
import { MapContext } from "../MapProvider";

import { Container, Label, Row } from "./LayerSelection.styles";

export interface IButtonProps {
  title?: string;
  icon?: React.ReactNode;
  mode?: string;
}

interface LayerNode extends Node {
  isSublayer?: boolean;
}

const LayerSelection: React.FC = () => {
  const { drawControl, map, isDrawing } = useContext(MapContext);
  const versionId = useAppSelector((state) => state.project.versionId);
  const config = useAppSelector((state) => selectVersionConfig(state, versionId));
  const [selectedLayer, setSelectedLayer] = React.useState<string | undefined>(drawControl.current?.options?.editOptions?.editingLayerId);
  const [visibleLayers, setVisibleLayers] = useState<Set<string>>(new Set());
  const [editingLayers, setEditingLayers] = useState<Set<string>>(new Set());

  /**
   * Generates a list of editable layers based on the drawing control's option.allLayers and the project's version config.
   * Each layer is represented as a node with a value, label, and icon for use in the checkbox tree.
   * When ShowSublayers is true, sublayers are also included in the list.
   */
  const allLayers = useAppSelector((state) => {
    const layerConfigs: LayerNode[] = [];
    const layerOptions = drawControl.current?.options?.editOptions?.allLayers;

    if (!layerOptions || !config) return layerConfigs;

    layerOptions.forEach((layer) => {
      // Search through config.Data.entities to find a Layer or Sublayer with matching LayerKey
      const layerConfig = config?.Data.entities
        ? (Object.values(config.Data.entities).find((entity) => {
            const e = entity as LayerConfig | SublayerConfig;
            return (e?.Type === "LAYER" || e?.Type === "SUBLAYER") && e?.Key === layer.ID;
          }) as LayerConfig | undefined)
        : undefined;

      if (layerConfig) {
        layerConfigs.push({
          value: layer.ID,
          label: layerConfig?.Label,
          icon: <InferredIcon entity={layerConfig} config={config} />,
          isSublayer: false,
        });

        // If ShowSublayers is true, add sublayers to the list
        if (layer.ShowSublayers && layerConfig.Children && layerConfig.Children.length > 0) {
          layerConfig.Children.forEach((sublayerId) => {
            const sublayerConfig = config.Data.entities[sublayerId] as SublayerConfig;
            if (sublayerConfig && sublayerConfig.Type === "SUBLAYER") {
              layerConfigs.push({
                value: sublayerId,
                label: sublayerConfig.Label,
                icon: <InferredIcon entity={sublayerConfig} config={config} />,
                isSublayer: true,
              });
            }
          });
        }
      }
    });

    return layerConfigs;
  });

  useEffect(() => {
    setSelectedLayer(drawControl.current?.options?.editOptions?.editingLayerId);
  }, [drawControl, drawControl.current?.options?.editOptions?.editingLayerId, map]);

  // Initialize all layers as visible (only once when empty)
  useEffect(() => {
    if (allLayers.length > 0 && visibleLayers.size === 0) {
      const allLayerIds = allLayers.map((layer) => layer.value);
      setVisibleLayers(new Set(allLayerIds));

      // Store in drawControl options for snapGetFeatures
      if (drawControl.current?.options) {
        drawControl.current.options.visibleLayerIds = allLayerIds;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allLayers.length]);

  // Update map layer visibility when visibleLayers changes
  // Toggles visibility for layer-specific styles (including validation styles)
  // based on their ID format: <layerId>-<styleId>
  useEffect(() => {
    if (!map) return;

    const style = map.getStyle();
    if (!style) return;

    // Update layer-specific drawing styles and validation styles
    style.layers.forEach((layer) => {
      try {
        // Handle layer-specific styles (e.g., "output/data_prep/demand-<styleId>.cold")
        // and validation styles (e.g., "output/data_prep/demand-gl-draw-point-invalid")
        allLayers.forEach(({ value: layerId, isSublayer }) => {
          // For sublayers, we need to get the parent layer key
          let layerKey = layerId;

          if (isSublayer) {
            // For sublayers, the value is the sublayer ID (config entity ID)
            // We need to find the parent layer key
            const sublayerConfig = config?.Data.entities[layerId] as SublayerConfig | undefined;
            if (sublayerConfig && sublayerConfig.Key) {
              layerKey = sublayerConfig.Key;
            }
          }

          const layerPrefix = `${layerKey}-`;

          if (layer.id.startsWith(layerPrefix)) {
            // This is a custom layer-specific style (including validation) - toggle its visibility
            const isVisible = visibleLayers.has(layerId);
            map.setLayoutProperty(layer.id, "visibility", isVisible ? "visible" : "none");
          }
        });
      } catch (error) {
        // Ignore errors for layers that don't support these operations
        console.warn(`Could not update visibility for layer ${layer.id}:`, error);
      }
    });
  }, [map, visibleLayers, allLayers, config]);

  const handleToggleVisibility = (layerId: string) => {
    setVisibleLayers((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(layerId)) {
        newSet.delete(layerId);
      } else {
        newSet.add(layerId);
      }

      // Store visible layers in drawControl options so snapGetFeatures can access it
      if (drawControl.current?.options) {
        drawControl.current.options.visibleLayerIds = Array.from(newSet);
      }

      return newSet;
    });
  };

  const handleToggleEditing = (layerId: string) => {
    setEditingLayers((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(layerId)) {
        newSet.delete(layerId);
      } else {
        newSet.add(layerId);
      }
      return newSet;
    });
  };

  // Update drawControl options and fire event when editingLayers changes
  useEffect(() => {
    // Build sublayer metadata for editable sublayers
    const editableSublayers: Array<{
      sublayerId: string;
      parentLayerId: string;
      filter?: import("mapbox-gl").ExpressionSpecification;
    }> = [];

    Array.from(editingLayers).forEach((id) => {
      const sublayerConfig = config?.Data.entities[id] as SublayerConfig | undefined;
      if (sublayerConfig?.Type === "SUBLAYER" && sublayerConfig.Key) {
        editableSublayers.push({
          sublayerId: id,
          parentLayerId: sublayerConfig.Key,
          filter: sublayerConfig.FilterConfiguration?.Mapbox,
        });
      }
    });

    // Store editing layers and sublayer metadata in drawControl options
    if (drawControl.current?.options?.editOptions) {
      drawControl.current.options.editOptions.editingLayerIds = Array.from(editingLayers);
      drawControl.current.options.editOptions.editableSublayers = editableSublayers;
    }

    // Fire event to notify listeners of the change
    map?.fire("draw.editingLayersChange", {
      editingLayerIds: Array.from(editingLayers),
      editableSublayers,
    });
  }, [editingLayers, config, map, drawControl]);

  /**
   * Handles the event when a layer is selected in the radio button group.
   * Fires a custom event that other components (like MultiLayerEditHandler) will handle.
   * @param id - The ID of the layer or sublayer that has been selected.
   */
  const handleOnSelect = (id: string) => {
    if (drawControl.current && drawControl.current.options) {
      // Check if this is a sublayer by looking it up in the config
      const sublayerConfig = config?.Data.entities[id] as SublayerConfig | undefined;
      const isSublayer = sublayerConfig?.Type === "SUBLAYER";

      // For sublayers, use the sublayer's Key (which is copied from parent) as the parentLayerId
      const parentLayerId = isSublayer && sublayerConfig ? sublayerConfig.Key : id;

      // Find the editing layer using the parent layer ID
      const editingLayer = drawControl.current.options.editOptions?.allLayers?.find((layer) => layer.ID === parentLayerId);

      setSelectedLayer(id);

      // Fire a custom event to notify other components (MultiLayerEditHandler will handle the logic)
      map?.fire("draw.layerchange", {
        layerId: id,
        parentLayerId: isSublayer ? parentLayerId : undefined,
        geometryType: editingLayer?.GeometryType,
        editingLayer,
        sublayerFilter: isSublayer && sublayerConfig?.FilterConfiguration?.Mapbox ? sublayerConfig.FilterConfiguration.Mapbox : undefined,
      });
    }
  };

  if (!isDrawing || !drawControl.current?.options?.editOptions?.allLayers) return null;

  return (
    <Container>
      <Paper sx={{ p: 1 }} elevation={3}>
        {allLayers.map(({ label, value, icon, isSublayer }) => (
          <Row key={value} selected={selectedLayer === value} onClick={() => handleOnSelect(value)} sx={{ pl: isSublayer ? 2 : 0 }}>
            <Checkbox
              checked={visibleLayers.has(value)}
              onChange={(e) => {
                e.stopPropagation();
                handleToggleVisibility(value);
              }}
              size="small"
              sx={{ p: 0.5 }}
              title="Toggle layer visibility"
            />
            <Label editing={editingLayers.has(value)}>
              {icon}
              {label}
            </Label>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleToggleEditing(value);
              }}
              color={editingLayers.has(value) ? "primary" : "default"}
              title="Toggle layer editing"
              sx={{ ml: "auto", p: 0 }}
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Row>
        ))}
      </Paper>
    </Container>
  );
};

export default LayerSelection;
